import React, { useState, useEffect, useCallback } from 'react';
import propTypes from 'prop-types';
import { FieldArray } from 'formik';
import omit from 'lodash/omit';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import {
  getPermLabel,
  extPermissions,
  rolePermissions,
  changePermission,
  getAssignableUserTypes,
  getHiddenPermissions,
  getPreservedPermissions,
  isAvailabilityEnabled,
  isScheduleOnly,
  filterSettingsTimeclockPermissions,
  dependantPermissions
} from 'common/permissions';
import IOSSwitch from 'components/IOSSwitch';
import { isIOS, Option } from 'components/Option';
import UserTypeInfo from './components/UserTypeInfo';

import styles from './styles';
import LocationAccessPermission from 'components/LocationAccessPermission/LocationAccessPermission';
import { useSelector } from 'react-redux';
import { hasPermissionsFor } from 'helpers/_helpers';


const UserPermissions = ({
  classes,
  values,
  setFieldValue,
  handleChange,
  currentUserId,
  errors,
  setErrors,
}) => {
  const [permissions, setPermissions] = useState({});
  const locationSettings = useSelector(store => store.personalProfile?.organizationLocation?.settings);
  const organization = useSelector(store => store.personalProfile?.organization);
  const orgSettings = organization?.settings;
  const scheduleOnly = isScheduleOnly(organization);

  const updatePermissions = (role) => {
    if (!extPermissions[role]) {
      return setPermissions({});
    }

    let userPermissions = extPermissions[role];
    if (!locationSettings?.timeclock) {
      userPermissions = userPermissions.filter(permission => !filterSettingsTimeclockPermissions.includes(permission));
    }
    if (!orgSettings?.generateCertificates) {
      userPermissions = userPermissions.filter(permission => permission !== 'materialCertification');
    }
    if (!orgSettings?.multiLocations) {
      userPermissions = userPermissions.filter(permission => permission !== 'multiLocations');
    }
    const hidden = getHiddenPermissions(locationSettings);
    userPermissions = userPermissions.filter(permission => !hidden.includes(permission));

    const nextPermissions = userPermissions.reduce((res, permission) => ({ ...res, [permission]: !!values.permissions[permission] }), {});

    setFieldValue(`permissions`, nextPermissions);
    setPermissions(omit(nextPermissions, getPreservedPermissions(organization)))
  }

  useEffect(() => {
    updatePermissions(values?.userType);
  }, [values?.userType, locationSettings, orgSettings, organization]);


  const handleChangePermission = useCallback((permission) => () => {
    //* changePermission's dependency cascade writes the coupled kiosk key into its result — a preserved one must never come back
    const newPermissions = omit(changePermission(values.userType, permissions, permission), getPreservedPermissions(organization));
    if (['scheduleFullAccess', 'scheduleEdit'].includes(permission) && !newPermissions[permission]) {
      newPermissions['dwlVerification'] = false;
    }

    setPermissions({ ...newPermissions });
    setFieldValue('permissions', { ...values.permissions, ...newPermissions });
  }, [values, permissions, organization, setFieldValue]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <label htmlFor="userType">
            <Typography variant="body1" color="textSecondary">
              User type:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  id="userType"
                  name="userType"
                  value={values.userType}
                  onChange={handleChange}
                  className={classes.textField}
                  variant="outlined"
                  inputProps={{ 'aria-label': 'User type' }}
                  fullWidth
                  SelectProps={{
                    native: isIOS,
                  }}
                  disabled={values.userId === currentUserId}
                >
                  {getAssignableUserTypes(organization, values.userType).map((type) => (
                    <Option key={type} value={type}>
                      {type}
                    </Option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={8}>
                <UserTypeInfo
                  type={values.userType}
                  isScheduleOnly={scheduleOnly}
                  availabilityEnabled={isAvailabilityEnabled(locationSettings)}
                />
              </Grid>
            </Grid>
          </label>
        </Grid>
      </Grid>
      <Divider className={classes.divider} />
      {Object.keys(permissions).length !== 0 && (
        <Grid
          container
          spacing={2}
          className={classes.marginSmall}
          justifyContent="space-between"
        >
          {Object.keys(permissions)
            .sort((a, b) => a.localeCompare(b))
            .map((permission) => (
              <Grid
                item
                xs={12}
                md={5}
                lg={5}
                key={`__user-permission__${permission}__`}
              >
                <FormControlLabel
                  label={getPermLabel(permission, { isScheduleOnly: scheduleOnly }) + ':'}
                  labelPlacement="start"
                  control={
                    <IOSSwitch
                      checked={permissions[permission]}
                      onChange={handleChangePermission(permission)}
                      name={permission}
                      value={permissions[permission]}
                      disabled={
                        values.userId === currentUserId ||
                        (dependantPermissions[permission] && dependantPermissions[permission]?.every(p => !permissions[p] && !rolePermissions[values.userType][p]))
                      }
                    />
                  }
                />
              </Grid>
            ))}
        </Grid>
      )}
      {/* {permissions.multiLocations && <Divider />} */}
      {permissions.multiLocations && (  
        <Grid
          container
        >
          <FieldArray name="assignedToLocations">
            {({ remove, push, move, replace }) => (
              <LocationAccessPermission
                homeBaseLocationId={values.homeBaseLocationId}
                useSettingsLocations={true}
                values={values.assignedToLocations}
                remove={remove}
                push={push}
                move={move}
                replace={replace}
                setFieldValue={setFieldValue}
                error={errors?.assignedToLocations}
                onChange={() => setErrors({ 'assignedToLocations': '' })}
              />
            )}
          </FieldArray>
        </Grid>
      )}
    </>
  )
}

UserPermissions.propTypes = {
  classes: propTypes.object.isRequired,
  values: propTypes.object,
  handleChange: propTypes.func,
  setFieldValue: propTypes.func,
  currentUserId: propTypes.string.isRequired,
};

export default withStyles(styles)(UserPermissions);
